import { GameObjects, Scene } from 'phaser';
import { COLORS, SIZES, GAME_BALANCE } from '../config/Constants';

export class Base extends GameObjects.GameObject {
  private graphics: GameObjects.Graphics | null = null;
  private baseLine: GameObjects.Image | null = null;
  private health: number;
  private maxHealth: number;
  public baseLineY: number;

  constructor(scene: Scene, baseLineY: number, initialHealth: number = 100) {
    super(scene, 'Base');
    
    this.baseLineY = baseLineY;
    this.health = initialHealth;
    this.maxHealth = initialHealth;
    
    this.createBaseGraphic();
    scene.add.existing(this);
    // No physics body needed for base
  }

  private createBaseGraphic(): void {
    this.graphics = this.scene.add.graphics();
    
    this.updateBaseVisual();
  }

  private updateBaseVisual(): void {
    if (!this.graphics) return;
    
    const { width } = this.scene.cameras.main;
    
    this.graphics.clear();
    
    // Base line (single cyan line)
    this.graphics.lineStyle(3, COLORS.BASE_BORDER, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(0, this.baseLineY);
    this.graphics.lineTo(width, this.baseLineY);
    this.graphics.strokePath();
    
    // Health bar background
    const healthBarY = this.baseLineY + 15;
    const healthBarWidth = width * 0.8;
    const healthBarX = (width - healthBarWidth) / 2;
    
    this.graphics.fillStyle(COLORS.HEALTH_BACKGROUND, 1);
    this.graphics.fillRect(healthBarX, healthBarY, healthBarWidth, SIZES.BASE_HEALTH_BAR_HEIGHT);
    
    // Health bar fill
    const healthPercentage = this.health / this.maxHealth;
    const healthColor = healthPercentage > GAME_BALANCE.HEALTH_HIGH_THRESHOLD ? COLORS.HEALTH_HIGH : 
                       healthPercentage > GAME_BALANCE.HEALTH_LOW_THRESHOLD ? COLORS.HEALTH_MEDIUM : COLORS.HEALTH_LOW;
    
    this.graphics.fillStyle(healthColor, 1);
    this.graphics.fillRect(healthBarX, healthBarY, healthBarWidth * healthPercentage, SIZES.BASE_HEALTH_BAR_HEIGHT);
    
    // Border
    this.graphics.lineStyle(1, COLORS.HEALTH_BORDER, 1);
    this.graphics.strokeRect(healthBarX, healthBarY, healthBarWidth, SIZES.BASE_HEALTH_BAR_HEIGHT);
    
    // Base text
    this.scene.add.text(10, this.baseLineY + 10, 'BASE', {
      fontSize: '12px',
      fontFamily: 'Courier New',
      color: '#00ffff'
    });
  }



  public takeDamage(damage: number): void {
    this.health = Math.max(0, this.health - damage);
    this.updateBaseVisual();
    
    if (this.health <= 0) {
      this.onBaseDestroyed();
    }
  }

  public heal(amount: number): void {
    this.health = Math.min(this.maxHealth, this.health + amount);
    this.updateBaseVisual();
  }

  public getHealth(): number {
    return this.health;
  }

  public getMaxHealth(): number {
    return this.maxHealth;
  }

  public getHealthPercentage(): number {
    return this.health / this.maxHealth;
  }

  public isDestroyed(): boolean {
    return this.health <= 0;
  }

  private onBaseDestroyed(): void {
    // Trigger game over
    this.scene.events.emit('base-destroyed');
  }

  public destroy(): void {
    if (this.graphics) {
      this.graphics.destroy();
    }
    super.destroy();
  }
}